import { useState } from 'react'
import { Folder, FolderX, Search } from 'lucide-react'
import { useBrowseDivisions } from '@/hooks/usePlanBrowse'
import { useDebounce } from '@/hooks/useDebounce'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FolderCard } from './FolderCard'
import { FolderGrid, FolderGridSkeleton } from './FolderGrid'
import type { BrowseDivisionSelection } from '@/stores/useBrowseStore'

interface DivisionGridProps {
  onSelect: (division: BrowseDivisionSelection) => void
}

export function DivisionGrid({ onSelect }: DivisionGridProps) {
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)
  const { data, isLoading, isError, isFetching, refetch } = useBrowseDivisions(
    debouncedSearch || undefined
  )
  const divisions = data?.data ?? []

  return (
    <div className="flex flex-col gap-4">
      <div className="relative max-w-[352px]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search divisions..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="pl-9 bg-muted/50"
        />
      </div>

      {isLoading ? (
        <FolderGridSkeleton label="Loading divisions" />
      ) : isError ? (
        <div role="alert" className="flex flex-col items-center gap-2 py-16">
          <p className="text-sm text-muted-foreground">
            Failed to load divisions
          </p>
          <Button
            variant="outline"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </div>
      ) : divisions.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
          <FolderX className="h-10 w-10" />
          <p className="text-sm">No divisions found</p>
        </div>
      ) : (
        <FolderGrid>
          {divisions.map(division => (
            <FolderCard
              key={division.id}
              icon={<Folder className="h-6 w-6" />}
              title={division.name}
              subtitle={`${division.plan_count} plan${division.plan_count !== 1 ? 's' : ''}`}
              onClick={() =>
                onSelect({
                  id: division.id,
                  name: division.name,
                  code: division.code,
                })
              }
            />
          ))}
        </FolderGrid>
      )}
    </div>
  )
}
